/**
 * Admin page with flat tabs for managing all admin entities.
 *
 * Restructured from 7 tabs to 12 flat tabs for improved UX:
 * 1. Users
 * 2. User Groups
 * 3. Roles
 * 4. Data Types
 * 5. Devices
 * 6. Clinics
 * 7. Studies
 * 8. Datasets
 * 9. Associations
 * 10. Policies
 * 11. Access Exploration
 * 12. Entity Sync
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs, Container, Button } from 'react-bootstrap';

// Identity & Access Management
import UsersManagement from '@/components/admin/UsersManagement';
import UserGroupsManagement from '@/components/admin/UserGroupsManagement';
import RolesManagement from '@/components/admin/RolesManagement';

// Data Entities
import DataTypesManagement from '@/components/admin/DataTypesManagement';
import DevicesManagement from '@/components/admin/DevicesManagement';
import ClinicsManagement from '@/components/admin/ClinicsManagement';
import StudiesManagement from '@/components/admin/StudiesManagement';

// Data Management
import DatasetsManagementSimplified from '@/components/admin/DatasetsManagementSimplified';
import AssociationsManagement from '@/components/admin/AssociationsManagement';
import PolicyManagement from '@/components/admin/policies/PolicyManagement';

// Tools
import AccessExploration from '@/components/admin/AccessExploration';
import EntitySync from '@/components/admin/EntitySync';

export default function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0">{t('admin.title')}</h1>
          <Button variant="outline-secondary" onClick={() => navigate('/home')}>
            <i className="fas fa-home me-2"></i>
            {t('admin.backToHome')}
          </Button>
        </div>

        {/* Tab navigation with visual grouping hints */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'users')}
          className="mb-4 admin-tabs flex-wrap"
          style={{ gap: '2px' }}
        >
          {/* Identity & Access Management */}
          <Tab eventKey="users" title={t('admin.users', 'Users')}>
            <UsersManagement />
          </Tab>
          <Tab eventKey="groups" title={t('admin.userGroups', 'User Groups')}>
            <UserGroupsManagement />
          </Tab>
          <Tab eventKey="roles" title={t('admin.roles', 'Roles')}>
            <RolesManagement />
          </Tab>

          {/* Data Entities */}
          <Tab eventKey="data-types" title={t('admin.dataTypes', 'Data Types')}>
            <DataTypesManagement />
          </Tab>
          <Tab eventKey="devices" title={t('admin.devices', 'Devices')}>
            <DevicesManagement />
          </Tab>
          <Tab eventKey="clinics" title={t('admin.clinics', 'Clinics')}>
            <ClinicsManagement />
          </Tab>
          <Tab eventKey="studies" title={t('admin.studies', 'Studies')}>
            <StudiesManagement />
          </Tab>

          {/* Data Management */}
          <Tab eventKey="datasets" title={t('admin.datasets', 'Datasets')}>
            <DatasetsManagementSimplified />
          </Tab>
          <Tab eventKey="associations" title={t('admin.associations', 'Associations')}>
            <AssociationsManagement />
          </Tab>
          <Tab eventKey="policies" title={t('admin.policies', 'Policies')}>
            <PolicyManagement />
          </Tab>

          {/* Tools */}
          <Tab eventKey="access" title={t('admin.accessExploration', 'Access Exploration')}>
            <AccessExploration />
          </Tab>
          <Tab eventKey="sync" title={t('admin.entitySync', 'Entity Sync')}>
            <EntitySync />
          </Tab>
        </Tabs>
      </Container>

      {/* Custom styles for admin tabs */}
      <style>
        {`
          .admin-tabs .nav-link {
            font-size: 0.9rem;
            padding: 0.5rem 0.75rem;
          }
          .admin-tabs .nav-item:nth-child(3)::after,
          .admin-tabs .nav-item:nth-child(7)::after,
          .admin-tabs .nav-item:nth-child(10)::after {
            content: '';
            display: inline-block;
            width: 1px;
            height: 20px;
            background-color: #dee2e6;
            margin-left: 8px;
            vertical-align: middle;
          }
          @media (max-width: 1200px) {
            .admin-tabs {
              overflow-x: auto;
              flex-wrap: nowrap !important;
              -webkit-overflow-scrolling: touch;
            }
            .admin-tabs .nav-item:nth-child(3)::after,
            .admin-tabs .nav-item:nth-child(7)::after,
            .admin-tabs .nav-item:nth-child(10)::after {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
}
